import { Trophy, RotateCcw, Copy, FileText, FileJson, Info, Sparkles } from 'lucide-react';
import type { MagicPrioritizationItem } from '../types';
import { URGENCY_LABELS, VALUE_LABELS, DURATION_LABELS } from '../types';

interface ResultsViewProps {
  items: MagicPrioritizationItem[];
  onReset: () => void;
}

export function ResultsView({ items, onReset }: ResultsViewProps) {
  // Sort items by weighted score (highest first)
  const sortedItems = [...items].sort((a, b) => {
    if (a.weightedScore === null) return 1;
    if (b.weightedScore === null) return -1;
    return b.weightedScore - a.weightedScore;
  });

  // Group items by CD3 category
  const highPriority = sortedItems.filter(i => i.cd3Category === 'high');
  const mediumPriority = sortedItems.filter(i => i.cd3Category === 'medium');
  const lowPriority = sortedItems.filter(i => i.cd3Category === 'low');

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case 'high': return 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'medium': return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'low': return 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-sec)]';
      default: return 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)]';
    }
  };

  const getCategoryBadgeColor = (category: string | null) => {
    switch (category) {
      case 'high': return 'bg-[var(--green)] text-white';
      case 'medium': return 'bg-[var(--mustard)] text-white';
      case 'low': return 'bg-[var(--surface)] text-white';
      default: return 'bg-[var(--rule-bright)] text-[var(--text-sec)]';
    }
  };

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = `# Magic Prioritization Trick Results\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        content += `Based on John Cutler's staged decoupling approach to surface Cost of Delay.\n\n`;
        content += `## Scoring Formula\n`;
        content += `Weighted CD3 = (Urgency × Value) / Duration\n\n`;
        content += `## Priority Categories\n\n`;
        
        if (highPriority.length > 0) {
          content += `### High Priority (Do First)\n`;
          highPriority.forEach((item, index) => {
            content += `${index + 1}. **${item.name}** - Score: ${item.weightedScore?.toFixed(2)}\n`;
            content += `   - Urgency: ${item.urgency ? URGENCY_LABELS[item.urgency].label : 'N/A'}\n`;
            content += `   - Value: ${item.value ? VALUE_LABELS[item.value].label : 'N/A'}\n`;
            content += `   - Duration: ${item.duration ? DURATION_LABELS[item.duration].label : 'N/A'}\n\n`;
          });
        }
        
        if (mediumPriority.length > 0) {
          content += `### Medium Priority (Important but Can Wait)\n`;
          mediumPriority.forEach((item, index) => {
            content += `${index + 1}. **${item.name}** - Score: ${item.weightedScore?.toFixed(2)}\n`;
            content += `   - Urgency: ${item.urgency ? URGENCY_LABELS[item.urgency].label : 'N/A'}\n`;
            content += `   - Value: ${item.value ? VALUE_LABELS[item.value].label : 'N/A'}\n`;
            content += `   - Duration: ${item.duration ? DURATION_LABELS[item.duration].label : 'N/A'}\n\n`;
          });
        }
        
        if (lowPriority.length > 0) {
          content += `### Low Priority (Defer or Reconsider)\n`;
          lowPriority.forEach((item, index) => {
            content += `${index + 1}. **${item.name}** - Score: ${item.weightedScore?.toFixed(2)}\n`;
            content += `   - Urgency: ${item.urgency ? URGENCY_LABELS[item.urgency].label : 'N/A'}\n`;
            content += `   - Value: ${item.value ? VALUE_LABELS[item.value].label : 'N/A'}\n`;
            content += `   - Duration: ${item.duration ? DURATION_LABELS[item.duration].label : 'N/A'}\n\n`;
          });
        }
        
        filename = `magic-prioritization-results-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
        
      case 'json':
        content = JSON.stringify({
          generated: new Date().toISOString(),
          method: 'Magic Prioritization Trick',
          formula: 'Weighted CD3 = (Urgency × Value) / Duration',
          categories: {
            high: highPriority,
            medium: mediumPriority,
            low: lowPriority,
          },
          rankedItems: sortedItems,
        }, null, 2);
        filename = `magic-prioritization-results-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
        
      case 'clipboard':
        content = `Magic Prioritization Results (${new Date().toLocaleDateString()})\n\n`;
        content += `HIGH PRIORITY:\n`;
        highPriority.forEach((item, i) => {
          content += `${i + 1}. ${item.name} (Score: ${item.weightedScore?.toFixed(2)})\n`;
        });
        content += `\nMEDIUM PRIORITY:\n`;
        mediumPriority.forEach((item, i) => {
          content += `${i + 1}. ${item.name} (Score: ${item.weightedScore?.toFixed(2)})\n`;
        });
        content += `\nLOW PRIORITY:\n`;
        lowPriority.forEach((item, i) => {
          content += `${i + 1}. ${item.name} (Score: ${item.weightedScore?.toFixed(2)})\n`;
        });
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Create the 27-cell matrix visualization
  const renderMatrix = () => {
    const urgencyLevels = ['asap', 'soon', 'whenever'] as const;
    const valueLevels = ['game-changer', 'optimizer', 'tweak'] as const;
    const durationLevels = ['1-3-months', '1-3-quarters', '1-3-years'] as const;

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Matrix Header */}
          <div className="mb-4 text-center">
            <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">27-Cell CD3 Matrix</h3>
            <p className="text-sm text-[var(--text-sec)]">Items are positioned by Urgency × Value, with Duration shown in columns</p>
          </div>

          {/* Matrix Grid */}
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-2 text-sm font-semibold text-[var(--text-sec)] bg-[var(--surface)] border border-[var(--rule)]" rowSpan={2}>
                  Urgency
                </th>
                <th className="p-2 text-sm font-semibold text-[var(--text-sec)] bg-[var(--surface)] border border-[var(--rule)]" rowSpan={2}>
                  Value
                </th>
                <th className="p-2 text-sm font-semibold text-[var(--text-sec)] bg-[var(--surface)] border border-[var(--rule)]" colSpan={3}>
                  Duration
                </th>
              </tr>
              <tr>
                {durationLevels.map((dur) => (
                  <th key={dur} className="p-2 text-xs font-medium text-[var(--text-sec)] bg-[var(--surface)] border border-[var(--rule)]">
                    {DURATION_LABELS[dur].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {urgencyLevels.map((urgency) => (
                valueLevels.map((value, vIdx) => (
                  <tr key={`${urgency}-${value}`}>
                    {vIdx === 0 && (
                      <td 
                        rowSpan={3} 
                        className={`p-2 text-sm font-medium border border-[var(--rule)] text-center align-middle ${
                          urgency === 'asap' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' :
                          urgency === 'soon' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                          'bg-[var(--surface)] text-[var(--text-sec)]'
                        }`}
                      >
                        {URGENCY_LABELS[urgency].label}
                      </td>
                    )}
                    <td 
                      className={`p-2 text-sm border border-[var(--rule)] ${
                        value === 'game-changer' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                        value === 'optimizer' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                        'bg-[var(--surface)] text-[var(--text-sec)]'
                      }`}
                    >
                      {VALUE_LABELS[value].label}
                    </td>
                    {durationLevels.map((duration) => {
                      const cellItems = items.filter(
                        item => item.urgency === urgency && 
                               item.value === value && 
                               item.duration === duration
                      );
                      // Determine cell background based on CD3 category
                      const cellCategory = cellItems.length > 0 ? cellItems[0].cd3Category : null;
                      return (
                        <td 
                          key={duration} 
                          className={`p-2 border border-[var(--rule)] min-h-[60px] align-top ${
                            cellCategory === 'high' ? 'bg-[var(--green-dim)]' :
                            cellCategory === 'medium' ? 'bg-[var(--mustard-dim)]' :
                            cellCategory === 'low' ? 'bg-[var(--surface)]' :
                            'bg-[var(--bg-raised)]'
                          }`}
                        >
                          {cellItems.length > 0 ? (
                            <div className="space-y-1">
                              {cellItems.map((item) => (
                                <div 
                                  key={item.id}
                                  className={`text-xs px-2 py-1 rounded border ${getCategoryColor(item.cd3Category)}`}
                                  title={`Score: ${item.weightedScore?.toFixed(2)}`}
                                >
                                  {item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-[var(--text-ter)] text-center">—</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header Card */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="h-8 w-8 text-[var(--text-ter)]" />
          <div>
            <h2 className="text-3xl font-bold text-[var(--text-pri)]">
              The Grand Reveal
            </h2>
            <p className="text-[var(--text-sec)]">
              Your items organized by weighted Cost of Delay (CD3)
            </p>
          </div>
        </div>

        {/* Insight Box */}
        <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[var(--text-sec)]">
              <strong>What happened?</strong> You've organized your work into three weighted cost of delay 
              categories—high, medium, and low. In theory, you should work on items in descending order. 
              Notice how items with the same urgency and value can have different priorities based on duration.
              <br /><br />
              <strong>Formula:</strong> Weighted CD3 = (Urgency × Value) / Duration
            </div>
          </div>
        </div>
      </div>

      {/* Priority Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* High Priority */}
        <div className="bg-[var(--bg-raised)] shadow-sm border-2 border-[var(--green)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[var(--green)] text-white text-xs font-bold rounded">HIGH</span>
            <span className="text-sm font-semibold text-[var(--text-pri)]">Do First ({highPriority.length})</span>
          </div>
          {highPriority.length > 0 ? (
            <ul className="space-y-2">
              {highPriority.map((item, index) => (
                <li key={item.id} className="flex items-start gap-2">
                  {index === 0 && <Trophy className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />}
                  <span className="text-sm text-[var(--text-pri)]">{item.name}</span>
                  <span className="text-xs text-[var(--text-ter)] ml-auto">{item.weightedScore?.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)] italic">No items in this category</p>
          )}
        </div>

        {/* Medium Priority */}
        <div className="bg-[var(--bg-raised)] shadow-sm border-2 border-[var(--mustard)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[var(--mustard)] text-white text-xs font-bold rounded">MEDIUM</span>
            <span className="text-sm font-semibold text-[var(--text-pri)]">Important ({mediumPriority.length})</span>
          </div>
          {mediumPriority.length > 0 ? (
            <ul className="space-y-2">
              {mediumPriority.map((item) => (
                <li key={item.id} className="flex items-start gap-2">
                  <span className="text-sm text-[var(--text-pri)]">{item.name}</span>
                  <span className="text-xs text-[var(--text-ter)] ml-auto">{item.weightedScore?.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)] italic">No items in this category</p>
          )}
        </div>

        {/* Low Priority */}
        <div className="bg-[var(--bg-raised)] shadow-sm border-2 border-[var(--rule)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[var(--slate)] text-white text-xs font-bold rounded">LOW</span>
            <span className="text-sm font-semibold text-[var(--text-pri)]">Defer ({lowPriority.length})</span>
          </div>
          {lowPriority.length > 0 ? (
            <ul className="space-y-2">
              {lowPriority.map((item) => (
                <li key={item.id} className="flex items-start gap-2">
                  <span className="text-sm text-[var(--text-pri)]">{item.name}</span>
                  <span className="text-xs text-[var(--text-ter)] ml-auto">{item.weightedScore?.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)] italic">No items in this category</p>
          )}
        </div>
      </div>

      {/* Full Results Table */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Detailed Rankings</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--surface)] border-b-2 border-[var(--rule)]">
                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-sec)]">Rank</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-sec)]">Item</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Urgency</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Value</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Duration</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Score</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Category</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, index) => {
                const rank = index + 1;
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-[var(--rule)] hover:bg-[var(--surface)] ${
                      rank === 1 ? 'bg-[var(--mustard-dim)]' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-[var(--text-pri)]">
                      {rank === 1 ? <Trophy className="h-5 w-5 text-[var(--mustard)] inline" /> : rank}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)]">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.urgency === 'asap' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' :
                        item.urgency === 'soon' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                        'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                      }`}>
                        {item.urgency ? URGENCY_LABELS[item.urgency].label : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.value === 'game-changer' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                        item.value === 'optimizer' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                        'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                      }`}>
                        {item.value ? VALUE_LABELS[item.value].label : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-[var(--text-sec)]">
                      {item.duration ? DURATION_LABELS[item.duration].label : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center font-bold text-[var(--text-ter)]">
                      {item.weightedScore !== null ? item.weightedScore.toFixed(2) : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getCategoryBadgeColor(item.cd3Category)}`}>
                        {item.cd3Category?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 27-Cell Matrix */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        {renderMatrix()}
      </div>

      {/* Export Options */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export Results</h3>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => handleExport('markdown')}
            className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <FileText className="h-5 w-5" />
            <span className="text-xs">Markdown</span>
          </button>
          <button
            onClick={() => handleExport('json')}
            className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <FileJson className="h-5 w-5" />
            <span className="text-xs">JSON</span>
          </button>
          <button
            onClick={() => handleExport('clipboard')}
            className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <Copy className="h-5 w-5" />
            <span className="text-xs">Clipboard</span>
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onReset}
          className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
        >
          <RotateCcw className="h-5 w-5" />
          Start Over
        </button>
      </div>
    </div>
  );
}
